import { useState } from 'react';
import { appendCapiumTag, capiumTagGroupsFor } from '@/core/models/capium-tags';
import type { NoteDialogData, NoteOption, NoteRow } from '@/core/models/notes.models';
import type { RichTextConfig } from '@/core/models/richText';
import { NoteTagsTab } from '@/features/settings-b/NoteTagsTab';
import { Checkbox } from '@/kit/Checkbox';
import type { DialogComponentProps } from '@/kit/dialog';
import { InputField } from '@/kit/FormField';
import { RequiredMark } from '@/kit/RequiredMark';
import { Radio, RadioGroup } from '@/kit/Radio';
import { RichTextEditor } from '@/kit/RichTextEditor';
import { Select } from '@/kit/Select';
import { FieldMessage, NoteDialogShell, useNoteSave } from './NoteDialogParts';

export interface TextNoteModel {
  noteKind: 'new' | 'extension';
  /** task-108: the statutory note an extension is printed under (BS_DEBTORS and so on). */
  parentNoteType: string | null;
  /** BUG-079: whether the extension prints above or below the note it extends. */
  extensionPosition: 'above' | 'below';
  title: string;
  contents: string;
  showOnAbbreviated: boolean;
  showOnFilleted: boolean;
  ixbrlTag: string;
  ixbrlContext: 'duration' | 'instant';
}

/** Ids as written in the Angular config ('fonts', 'orderedList', 'unorderedList' are not real angular-editor ids). */
const EDITOR_CONFIG: RichTextConfig = {
  editable: true,
  spellcheck: true,
  height: '200',
  minHeight: '0',
  maxHeight: 'auto',
  width: 'auto',
  minWidth: '0',
  translate: 'yes',
  enableToolbar: true,
  showToolbar: true,
  placeholder: 'Enter text here...',
  defaultParagraphSeparator: '',
  sanitize: true,
  toolbarPosition: 'bottom',
  toolbarHiddenButtons: [
    [
      'fontName',
      'fontSize',
      'backgroundColor',
      'strikeThrough',
      'customClasses',
      'insertHorizontalRule',
      'removeFormat',
      'heading',
      'link',
      'unlink',
      'insertImage',
      'insertVideo',
      'fonts',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      'justifyFull',
      'indent',
      'outdent',
      'orderedList',
      'unorderedList',
      'textColor',
    ],
  ],
};

/** Free-text note used by Gift Aid, Impairments, Intangible Assets, Events, Exceptional Item, Director's Loan and Additional Notes. */
export function GiftCardDialog({ data, dialogRef }: DialogComponentProps<NoteDialogData, NoteRow>) {
  const [model, setModel] = useState<TextNoteModel>(() => {
    const c = data?.note?.content ?? {};
    return {
      noteKind: c.noteKind === 'extension' ? 'extension' : 'new',
      parentNoteType: typeof c.parentNoteType === 'string' && c.parentNoteType ? c.parentNoteType : null,
      extensionPosition: c.extensionPosition === 'above' ? 'above' : 'below',
      title: c.title ?? '',
      contents: c.contents ?? '',
      showOnAbbreviated: !!c.showOnAbbreviated,
      showOnFilleted: !!c.showOnFilleted,
      ixbrlTag: c.ixbrlTag ?? '',
      ixbrlContext: c.ixbrlContext === 'instant' ? 'instant' : 'duration',
    };
  });
  const { isSaving, save, submitted } = useNoteSave(data, dialogRef);
  const titleMissing = model.title.trim().length === 0;

  const set = <K extends keyof TextNoteModel>(key: K, value: TextNoteModel[K]) =>
    setModel((current) => ({ ...current, [key]: value }));

  /**
   * task-108: the parent only means something on an extension, and only once one is picked, so it
   * is sent then and not otherwise. The radio can flip freely without losing the choice, and a new
   * note never carries a parent - the API drops one anyway.
   */
  const toContent = ({ parentNoteType, extensionPosition, ...rest }: TextNoteModel) =>
    rest.noteKind === 'extension' && parentNoteType ? { ...rest, parentNoteType, extensionPosition } : rest;

  return (
    <NoteDialogShell
      title={data?.title ?? 'Add Gift Cards'}
      isSaving={isSaving}
      onSave={() => save(() => toContent(model), !titleMissing)}
    >
      <div className="d-flex gap-15 pt-5">
        <div>
          <div className="form-field w-250">
            <p className="fw-bold">Type</p>
            <RadioGroup
              name="noteKind"
              value={model.noteKind}
              onChange={(value) => set('noteKind', value as TextNoteModel['noteKind'])}
            >
              <div className="d-flex align-center gap-15">
                <Radio value="new"> New Note </Radio>
                <Radio value="extension"> Extension Note </Radio>
              </div>
            </RadioGroup>
          </div>
          {/* task-108: an extension is printed under its parent note, so say which one. */}
          {model.noteKind === 'extension' && (
            <div className="form-field extension-of">
              <p className="fw-bold">Extension of</p>
              <Select<NoteOption>
                className="w-510"
                items={data?.parentNoteTypes ?? []}
                bindLabel="label"
                bindValue="value"
                placeholder="Select the note this extends"
                value={model.parentNoteType}
                onChange={(value) => set('parentNoteType', (value as string | null) ?? null)}
              />
              <div className="mt-5">
                <RadioGroup
                  name="extensionPosition"
                  value={model.extensionPosition}
                  onChange={(value) => set('extensionPosition', value as TextNoteModel['extensionPosition'])}
                >
                  <div className="d-flex align-center gap-15">
                    <Radio value="above"> Above Existing </Radio>
                    <Radio value="below"> Below Existing </Radio>
                  </div>
                </RadioGroup>
              </div>
              <p className="font-size-12 text-gray mb-0 mt-5">
                The title and text are printed above or below this note in the accounts.
              </p>
            </div>
          )}
          <div className="form-field">
            <p className="fw-bold">
              Title <RequiredMark />
            </p>
            <InputField
              fieldClassName="w-510"
              autoComplete="off"
              maxLength={255}
              invalid={submitted && titleMissing}
              value={model.title}
              onChange={(event) => set('title', event.target.value)}
            />
            <FieldMessage show={submitted && titleMissing}>Enter the note title.</FieldMessage>
          </div>
          <div className="form-field w-510">
            <p className="fw-bold">Note Contents</p>
            <RichTextEditor
              id="editor1"
              value={model.contents}
              onChange={(html) => set('contents', html)}
              config={EDITOR_CONFIG}
            />
          </div>
          <div className="d-flex gap-15">
            <Checkbox
              checked={model.showOnAbbreviated}
              onChange={(event) => set('showOnAbbreviated', event.target.checked)}
            >
              Show note on dormant accounts
            </Checkbox>
            <Checkbox checked={model.showOnFilleted} onChange={(event) => set('showOnFilleted', event.target.checked)}>
              Show note on filleted accounts
            </Checkbox>
          </div>
          <div className="form-field">
            <p className="fw-bold">iXBRL Tag</p>
            <InputField
              fieldClassName="w-250"
              autoComplete="off"
              charMode="alphanumeric"
              extraChars=":"
              maxLength={255}
              value={model.ixbrlTag}
              onChange={(event) => set('ixbrlTag', event.target.value)}
            />
          </div>
          <div className="form-field w-250">
            <p className="fw-bold">ixBRL Context</p>
            <RadioGroup
              name="ixbrlContext"
              value={model.ixbrlContext}
              onChange={(value) => set('ixbrlContext', value as TextNoteModel['ixbrlContext'])}
            >
              <div className="d-flex align-center gap-15">
                <Radio value="duration"> Duration </Radio>
                <Radio value="instant"> Instant </Radio>
              </div>
            </RadioGroup>
          </div>
        </div>
        {/* ap5-a5: the tags the accounts substitute; clicking one adds it to the wording. */}
        <NoteTagsTab
          groups={capiumTagGroupsFor(data?.clientType ?? (data?.isLlp ? 3 : null))}
          onInsert={(tag) => set('contents', appendCapiumTag(model.contents, tag))}
        />
      </div>
    </NoteDialogShell>
  );
}
